/**
 * Feature-level access gate for Contracts (CB-8.1, FR-70; RBAC-6.2, FR-21).
 *
 * This is the PURE decision half of the access seam: given whether the account has Contracts,
 * whether the person may read contracts, and whether they're a superadmin, decide whether they may
 * use the Contracts feature AT ALL. The `useContractsFeatureAccess()` hook wires this to
 * `PermissionContext` and the account registry; the direct-URL guard and the tab/action consumers
 * read the hook.
 *
 * RBAC-6.2 retired `tenant_settings.contract_access_role_ids` (20261103x2). Before it, the project tab
 * asked the permissions below while the tab body and the Contracts page asked the role list, so the
 * two could disagree. Now there is one answer, and it is the one `contracts_select` gives.
 *
 * Two load-bearing rules:
 *   1. CLOSED-BY-DEFAULT / FAILS CLOSED — an account switch that is off or unreadable denies everyone
 *      but a superadmin, whatever they hold.
 *   2. SUPERADMIN BYPASS — a superadmin is always allowed, so a misconfigured account can never lock
 *      the people who administer it out of the very surface that fixes it.
 *
 * Orthogonal to CB-8.2 management scope (`canManageContract` in contractActions.ts): that decides
 * "may this user manage THIS contract?"; this decides "may this user use Contracts at all?". A
 * surface must satisfy BOTH (feature-enabled AND in-scope) — keep them as separate inputs.
 */

/**
 * RBAC-7.3 · NFR-7b. Who may READ contract rows, which is what `contracts_select` and
 * `contract_events_select` test (`20261103b7`). Before that migration the policy had no role
 * condition at all, so the tab's permission check was the only thing hiding contracts.
 *
 * ⚠️ This list and the policy must say the same thing. `contractAccess.test.ts` reads the
 * migration and fails if they drift. Change both or neither.
 */
export const CONTRACT_READ_PERMISSIONS = ['view_contracts', 'manage_contracts', 'project.manage_all'] as const;

/** `holds_permission()` also answers true for `system.override`; the client's set does not, so it is named. */
export function canReadContracts(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission('system.override') || CONTRACT_READ_PERMISSIONS.some((p) => hasPermission(p));
}

export interface FeatureAccessInput {
  /**
   * RBAC-6.1 / FR-19. Whether Contracts is switched ON for the account
   * (`my_account_modules()`, KNK-1.55's registry). Required, so no caller can forget it. False or
   * null (unreadable) hides the feature from everyone but a superadmin.
   */
  accountEnabled: boolean | null | undefined;
  /** RBAC-6.2. `canReadContracts(hasPermission)`: the same question `contracts_select` asks. */
  canRead: boolean;
  /** Superadmin bypass — always allowed regardless of the account. */
  isSuperadmin: boolean;
}

/** Decide feature-level Contracts access: a superadmin, or a reader in an account that has Contracts. */
export function computeContractsFeatureAccess(input: FeatureAccessInput): boolean {
  if (input.isSuperadmin) return true; // rule 2 — superadmin always allowed
  if (input.accountEnabled !== true) return false; // rule 1 — the account switch comes first
  return input.canRead === true;
}
